/**
 * 🚀 REACT 19 ERROR RECOVERY UTILITIES
 * 
 * Enhanced error recovery with React 19 specific patterns
 * and improved error classification for better handling
 */

import React, { ComponentType, ReactNode } from 'react';

// React 19 error types
export type React19ErrorType = 'hydration' | 'async' | 'use-hook' | 'chunk-load' | 'runtime' | 'unknown';

// Enhanced error interface
export interface ClassifiedError extends Error {
  type: React19ErrorType;
  isRecoverable: boolean;
  retryCount: number;
  timestamp: string;
  context?: Record<string, unknown>;
}

// Enhanced error recovery options
export interface ErrorRecoveryOptions {
  maxRetries?: number;
  retryDelay?: number;
  fallbackAction?: () => void;
  enableAutoRecovery?: boolean;
  isolationLevel?: 'component' | 'page' | 'app';
}

// React 19 enhanced error recovery options
export interface React19ErrorRecoveryOptions extends ErrorRecoveryOptions {
  errorType?: React19ErrorType;
  useReportError?: boolean;
  customRecoveryStrategies?: Map<React19ErrorType, () => Promise<void>>;
}

/**
 * 🚀 React 19 Error Classification
 * Analyzes errors to determine their type and recovery potential
 */
export function classifyError(error: Error): ClassifiedError {
  let errorType: React19ErrorType = 'unknown';
  let isRecoverable = false;
  
  const errorMessage = error.message.toLowerCase();
  const errorStack = error.stack?.toLowerCase() || '';
  
  // Hydration errors (common in React 19)
  if (errorMessage.includes('hydration') || 
      errorMessage.includes('server') && errorMessage.includes('client') ||
      errorMessage.includes('text content does not match')) {
    errorType = 'hydration';
    isRecoverable = true;
  }
  
  // use() hook errors (React 19 specific)
  else if (errorMessage.includes('use()') || 
           errorStack.includes('use(') || 
           error.name === 'SuspenseException') {
    errorType = 'use-hook';
    isRecoverable = true;
  }
  
  // Chunk loading errors (code splitting)
  else if (error.name === 'ChunkLoadError' || 
           errorMessage.includes('loading chunk') ||
           errorMessage.includes('loading css chunk') ||
           errorMessage.includes('promise rejected')) {
    errorType = 'chunk-load';
    isRecoverable = true;
  }
  
  // Async errors
  else if (errorMessage.includes('async') || 
           errorMessage.includes('promise') ||
           errorMessage.includes('await')) {
    errorType = 'async';
    isRecoverable = true;
  }
  
  // Runtime errors
  else {
    errorType = 'runtime';
    isRecoverable = false;
  }
  
  return {
    ...error,
    name: error.name,
    message: error.message,
    stack: error.stack,
    type: errorType,
    isRecoverable,
    retryCount: 0,
    timestamp: new Date().toISOString(),
    context: {
      userAgent: typeof window !== 'undefined' ? window.navigator.userAgent : 'Unknown',
      url: typeof window !== 'undefined' ? window.location.href : 'Unknown',
      errorType,
      isRecoverable
    }
  } as ClassifiedError;
}

/**
 * 🚀 React 19 Error Recovery Manager
 * Centralized error recovery with intelligent retry strategies
 */
export class ErrorRecoveryManager {
  private recoveryStrategies: Map<React19ErrorType, () => Promise<void>>;
  private errorCounts: Map<React19ErrorType, number>;
  private readonly maxErrorsPerType = 5;
  
  constructor() {
    this.recoveryStrategies = new Map();
    this.errorCounts = new Map();
    this.initializeDefaultStrategies();
  }
  
  private initializeDefaultStrategies() {
    // Hydration error recovery
    this.recoveryStrategies.set('hydration', async () => {
      console.log('[React 19 Recovery] Attempting hydration error recovery');
      // Force a quick re-render to resolve hydration mismatch
      await new Promise(resolve => setTimeout(resolve, 100));
    });
    
    // use() hook error recovery
    this.recoveryStrategies.set('use-hook', async () => {
      console.log('[React 19 Recovery] Attempting use() hook error recovery');
      // Moderate delay to let the promise resolve
      await new Promise(resolve => setTimeout(resolve, 500));
    });
    
    // Chunk loading error recovery
    this.recoveryStrategies.set('chunk-load', async () => {
      console.log('[React 19 Recovery] Attempting chunk loading error recovery');
      // Longer delay and potentially reload modules
      await new Promise(resolve => setTimeout(resolve, 2000));
      // Note: In a real app, you might want to implement module reloading here
    });
    
    // Async error recovery
    this.recoveryStrategies.set('async', async () => {
      console.log('[React 19 Recovery] Attempting async error recovery');
      await new Promise(resolve => setTimeout(resolve, 1000));
    });
  }
  
  /**
   * Add custom recovery strategy for specific error types
   */
  addRecoveryStrategy(errorType: React19ErrorType, strategy: () => Promise<void>) {
    this.recoveryStrategies.set(errorType, strategy);
  }
  
  /**
   * Attempt recovery for a classified error
   */
  async attemptRecovery(error: ClassifiedError): Promise<boolean> {
    const errorType = error.type;
    const currentCount = this.errorCounts.get(errorType) || 0;
    
    // Check if we've hit the error limit for this type
    if (currentCount >= this.maxErrorsPerType) {
      console.warn(`[React 19 Recovery] Max errors reached for ${errorType}, skipping recovery`);
      return false;
    }
    
    // Increment error count
    this.errorCounts.set(errorType, currentCount + 1);
    
    const strategy = this.recoveryStrategies.get(errorType);
    if (!strategy) {
      console.warn(`[React 19 Recovery] No recovery strategy for ${errorType}`);
      return false;
    }
    
    try {
      await strategy();
      console.log(`[React 19 Recovery] Successfully recovered from ${errorType} error`);
      return true;
    } catch (recoveryError) {
      console.error(`[React 19 Recovery] Recovery failed for ${errorType}:`, recoveryError);
      return false;
    }
  }
  
  /**
   * Reset error counts (useful for testing or after successful periods)
   */
  resetErrorCounts() {
    this.errorCounts.clear();
  }
  
  /**
   * Get current error statistics
   */
  getErrorStats(): Record<React19ErrorType, number> {
    const stats: Record<string, number> = {};
    for (const [type, count] of this.errorCounts.entries()) {
      stats[type] = count;
    }
    return stats as Record<React19ErrorType, number>;
  }
}

// Global error recovery manager instance
export const errorRecoveryManager = new ErrorRecoveryManager();

/**
 * Enhanced error recovery with React 19 patterns
 */
export async function withErrorRecovery<T>(
  action: () => Promise<T>,
  options: ErrorRecoveryOptions = {}
): Promise<T | null> {
  const { maxRetries = 3, retryDelay = 1000, fallbackAction } = options;
  
  let lastError: Error | null = null;
  
  for (let attempt = 0; attempt < maxRetries; attempt++) {
    try {
      return await action();
    } catch (error) {
      lastError = error instanceof Error ? error : new Error(String(error));
      if (attempt < maxRetries - 1) {
        await new Promise(resolve => setTimeout(resolve, retryDelay * (attempt + 1)));
      }
    }
  }
  
  if (fallbackAction) {
    fallbackAction();
  }
  
  console.error('Error recovery failed after', maxRetries, 'attempts:', lastError);
  return null;
}

/**
 * 🚀 React 19 Enhanced Error Recovery
 * Includes error classification and intelligent retry strategies
 */
export async function withReact19ErrorRecovery<T>(
  action: () => Promise<T>,
  options: React19ErrorRecoveryOptions = {}
): Promise<T | null> {
  const { 
    maxRetries = 3, 
    retryDelay = 1000, 
    fallbackAction,
    enableAutoRecovery = true,
    useReportError = true,
    customRecoveryStrategies
  } = options;
  
  let lastError: ClassifiedError | null = null;
  
  for (let attempt = 0; attempt < maxRetries; attempt++) {
    try {
      return await action();
    } catch (error) {
      // Classify the error
      const errorInstance = error instanceof Error ? error : new Error(String(error));
      const classifiedError = classifyError(errorInstance);
      classifiedError.retryCount = attempt + 1;
      lastError = classifiedError;
      
      // Use React 19's reportError API if available
      if (useReportError && typeof window !== 'undefined' && 'reportError' in window) {
        window.reportError(classifiedError);
      }
      
      // Attempt auto-recovery if enabled and error is recoverable
      if (enableAutoRecovery && classifiedError.isRecoverable && attempt < maxRetries - 1) {
        console.log(`[React 19 Recovery] Attempting auto-recovery for ${classifiedError.type} error (attempt ${attempt + 1}/${maxRetries})`);
        
        // Use custom recovery strategy if provided
        if (customRecoveryStrategies?.has(classifiedError.type)) {
          try {
            await customRecoveryStrategies.get(classifiedError.type)!();
          } catch (recoveryError) {
            console.warn(`[React 19 Recovery] Custom recovery failed:`, recoveryError);
          }
        } else {
          // Use default recovery manager
          await errorRecoveryManager.attemptRecovery(classifiedError);
        }
        
        // Add additional delay based on error type
        let recoveryDelay = retryDelay;
        if (classifiedError.type === 'hydration') {
          recoveryDelay = 100; // Quick recovery for hydration
        } else if (classifiedError.type === 'chunk-load') {
          recoveryDelay = 2000; // Longer delay for chunk loading
        } else if (classifiedError.type === 'use-hook') {
          recoveryDelay = 500; // Medium delay for use() hook
        }
        
        await new Promise(resolve => setTimeout(resolve, recoveryDelay));
      } else {
        // Non-recoverable error or auto-recovery disabled
        if (attempt < maxRetries - 1) {
          await new Promise(resolve => setTimeout(resolve, retryDelay * (attempt + 1)));
        }
      }
    }
  }
  
  if (fallbackAction) {
    fallbackAction();
  }
  
  console.error(`[React 19 Recovery] Recovery failed after ${maxRetries} attempts:`, lastError);
  return null;
}

/**
 * 🚀 React 19 Error Recovery Hook
 * Hook for components to use error recovery
 */
export function useErrorRecovery() {
  const [errorState, setErrorState] = React.useState<{
    error: ClassifiedError | null;
    isRecovering: boolean;
    retryCount: number;
  }>({
    error: null,
    isRecovering: false,
    retryCount: 0
  });
  
  // React Compiler automatically optimizes - no useCallback needed
  const recoverFromError = async (error: Error) => {
    const classifiedError = classifyError(error);
    
    setErrorState({
      error: classifiedError,
      isRecovering: true,
      retryCount: 0
    });
    
    const recovered = await errorRecoveryManager.attemptRecovery(classifiedError);
    
    setErrorState(prev => ({
      ...prev,
      isRecovering: false,
      retryCount: prev.retryCount + 1
    }));
    
    return recovered;
  };
  
  // React Compiler automatically optimizes - no useCallback needed
  const resetError = () => {
    setErrorState({
      error: null,
      isRecovering: false,
      retryCount: 0
    });
  };
  
  return {
    error: errorState.error,
    isRecovering: errorState.isRecovering,
    retryCount: errorState.retryCount,
    recoverFromError,
    resetError
  };
}

/**
 * 🚀 React 19 Concurrent Error Recovery HOC
 * Higher-order component for automatic error recovery
 */
export function withConcurrentErrorRecovery<P extends object>(
  Component: ComponentType<P>,
  options: React19ErrorRecoveryOptions = {}
): ComponentType<P> {
  return function ConcurrentErrorRecoveryWrapper(props: P) {
    const { error, isRecovering, recoverFromError, resetError } = useErrorRecovery();
    
    React.useEffect(() => {
      // Reset error state when component mounts
      resetError();
    }, [resetError]);
    
    if (error && !isRecovering) {
      return React.createElement('div', {
        className: "p-4 bg-red-50 border border-red-200 rounded-lg"
      }, React.createElement('div', {
        className: "flex items-start"
      }, React.createElement('div', {
        className: "ml-3"
      }, React.createElement('h3', {
        className: "text-sm font-medium text-red-800"
      }, `Eroare ${error.type}: ${error.message}`), React.createElement('div', {
        className: "mt-2"
      }, React.createElement('button', {
        onClick: () => recoverFromError(error),
        className: "bg-red-100 hover:bg-red-200 text-red-800 font-medium py-1 px-3 rounded text-sm"
      }, "Încearcă din nou")))));
    }
    
    if (isRecovering) {
      return React.createElement('div', {
        className: "p-4 bg-blue-50 border border-blue-200 rounded-lg"
      }, React.createElement('div', {
        className: "flex items-center"
      }, React.createElement('div', {
        className: "animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"
      }), React.createElement('span', {
        className: "ml-2 text-sm text-blue-800"
      }, "Recuperare în curs...")));
    }
    
    return React.createElement(Component, props);
  };
}

// Legacy exports for backward compatibility
export function createErrorRecoveryHandler(options: ErrorRecoveryOptions = {}) {
  return <T,>(action: () => Promise<T>) => withErrorRecovery(action, options);
}